import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useConsentPolicy = () => {
  const [active, setActive] = useState("importance");
  const { brandName, normalizedBrand } = getBrandName();

  const sections = [
    { id: "importance", title: `Why Consent Matters at ${brandName}` },
    { id: "dashboard", title: "What You Can Control in the Consent Dashboard" },
    { id: "withdrawal", title: "What Happens If You Withdraw Consent?" },
    { id: "sensitive", title: "Special Note on Sensitive Data and Minors" },
    { id: "tracking", title: "How We Record & Track Consent" },
    { id: "requirements", title: "Where Consent Is Required" },
    { id: "manage", title: "How to Exercise or Manage Your Consent" },
    { id: "updates", title: "Updates to This Consent Policy" },
    { id: "questions", title: "Questions or Support" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    if (id === "dashboard") {
      return (
        <>
          <p style={{ marginBottom: "1rem" }}>
            Our Consent Management Dashboard offers user-friendly controls so you can make informed decisions. Here’s what you can do at any time:
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              fontWeight: "bold",
              paddingBottom: "0.5rem",
              borderBottom: "1px solid #ccc",
              marginBottom: "1rem",
            }}
          >
            <div>You Can...</div>
            <div>Examples</div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              rowGap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div><strong>✅ Grant or Deny Consent</strong></div>
            <div>For email updates, performance analytics, and personalization</div>

            <div><strong>🔄 Update Your Preferences</strong></div>
            <div>Turn off newsletter consent or switch off marketing cookies</div>

            <div><strong>❌ Withdraw Consent Immediately</strong></div>
            <div>Revoke permission with a single click</div>

            <div><strong>📜 View Consent History</strong></div>
            <div>See what you’ve approved, when, and for which purposes</div>

            <div><strong>📝 Request Support or Clarification</strong></div>
            <div>Understand each feature before making a decision</div>
          </div>

          <p className="text-muted">
            Our dashboard uses clear labels, plain language, and no hidden toggles — designed for real privacy control, not legal loopholes.
          </p>
        </>
      );
    }

    const contentMap = {
      importance: `
Consent is the legal and ethical foundation of our data practices. Before collecting or using your personal data, we will clearly explain:

✅ What data is being collected  
✅ Why is it needed  
✅ How it will be used or shared  
✅ With whom it may be shared  
✅ How long will we retain it

We never assume your consent. You always have the freedom to opt in or opt out without coercion. Your decision will never affect your core access to ${brandName}’s services unless the data is essential for specific functionality (which will be disclosed clearly).
`,

      withdrawal: `
You have the right to withdraw your consent at any time. Here’s what happens when you do:

• We immediately stop collecting or processing the data you withdrew consent for  
• Any non-essential features tied to that data (like personalization or tracking) will be paused or disabled  
• You will not be penalized in any way  
• If the data is essential for a service (like login or verification), we’ll inform you upfront  
• We log consent withdrawals securely and apply them across all ${brandName} systems connected to your account
`,

      sensitive: `
We apply stricter standards for sensitive and minor-related data, in line with Indian law.

a. Sensitive Personal Data  
We only collect data such as:
• Financial details  
• Health-related data  
• Biometric or location tracking

...with your explicit and informed consent. You will always see:
• What is being collected  
• Why it’s needed  
• A separate opt-in mechanism
`,

      tracking: `
${brandName} uses a robust Consent Logging System to ensure compliance and traceability:

• Timestamped records of when and how consent was given  
• Details of the specific options or services consented to  
• Logs of any consent updates or withdrawals  
• Encrypted records maintained securely for audit and legal use
`,

      requirements: `
We ask for your consent when:

• Sending promotional or marketing messages  
• Collecting non-essential usage analytics  
• Personalizing content, dashboards, or offers  
• Sharing your information with external service providers or partners  
• Collecting sensitive personal data

For essential functions like account setup, billing, or legal compliance, consent may not be required — but we still keep you informed through notices.
`,

      manage: `
You can control your consent preferences in the following ways:

a. Consent Management Dashboard  
Log in to your ${brandName} account and visit the “Privacy Settings” section to change your preferences instantly.

b. Via Email or Support  
Email us at 📧 ${brandName.toLowerCase()}@gmail.com or contact our Grievance Officer to make manual requests.

c. Cookie Banner & Preferences  
When you visit our website, use the cookie settings panel to accept or reject non-essential cookies.
`,

      updates: `
We may update this policy if:

• There’s a change in Indian or international privacy law (e.g., DPDP, GDPR)  
• We add new features that require new consent types  
• Our data handling practices change

We will notify you via the platform or email and will not assume new consent automatically — you will always have the right to re-evaluate.
`,

      questions: `
If you're unsure about your consent settings or wish to request guidance, our team is here to assist.

• Support Email: ${normalizedBrand}@gmail.com  
• Help Center / Dashboard: +91-9424847009

**Transparency. Simplicity. Control.**  
At ${brandName}, your privacy choices are respected, your rights are honored, and your data is protected — always.
`,
    };

    const lines = contentMap[id]?.split("\n") || [];
    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (line.includes("|")) {
        flushList();
        const [left, right] = line.split("|").map((s) => s.trim());
        contentElements.push(
          <p key={`row-${index}`}>
            <strong>{left}</strong>: {right}
          </p>
        );
      } else if (/^•|✅|🔄|❌|📜|📝/.test(line)) {
        items.push(line.replace(/^•\s*/, ""));
      } else if (/^[a-z]\./i.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
